import React, { useEffect, useRef, useState } from "react"
import {
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react"
import { useForm, useWatch } from "react-hook-form"
import SimpleBar from "simplebar-react"
import { useHistory } from "react-router-dom"
import Modal from "components/dialogs/Modal"
import FullScreenModalNav from "components/dialogs/FullScreenModalNav"
import Link from "components/elements/Link"
import Icon from "components/elements/Icon"
import Checkbox from "components/ReactHookForm/Checkbox"
import walletPassUploadsUrl from "api/walletPass"
import { post } from "services/api"
import { DioboxEvent } from "sharedTypes"
import useCurrentAccount from "services/useCurrentAccount"
import { errorToast, infoToast } from "utilities/toasts"
import {
  DEFAULT_GUEST_NAME_LABEL,
  PASS_EVENT_LOCATION_MODE_OPTIONS,
  PASS_BRAND_NAME_MODE_OPTIONS,
  PASS_EVENT_TITLE_MODE_OPTIONS,
  PASS_HEADER_MODE_OPTIONS,
  WalletPassBuilderFormValues,
} from "./walletPassBuilderTypes"
import WALLET_PASS_BUILDER_COPY from "./walletPassBuilderCopy"
import {
  formValuesToWalletPassData,
  walletPassDataToFormValues,
  formatEventAddress,
  formatEventDate,
  formatEventTime,
} from "./walletPassDataMappers"
import { PassBuilderSection } from "./PassBuilderLayout"
import { PassBuilderHelpText } from "./PassBuilderTypography"
import PassBuilderFieldBlock from "./PassBuilderFieldBlock"
import { SECTION_TITLE_MB_COLORS, SUBSECTION_GAP } from "./passBuilderTokens"
import PassBuilderBrandingSection from "./PassBuilderBrandingSection"
import PassBuilderColorField from "./PassBuilderColorField"
import {
  PassBuilderLabelRow,
  PassBuilderSourceValueRow,
  PassBuilderTextValueRow,
} from "./PassBuilderRows"
import PassBuilderFieldMode from "./PassBuilderFieldMode"
import PassBuilderLogoText from "./PassBuilderLogoText"
import PassBuilderAuxiliaryField from "./PassBuilderAuxiliaryField"
import PassBuilderQrToggle from "./PassBuilderQrToggle"
import PassBuilderBackSections from "./PassBuilderBackSections"
import WalletPassBuilderPreview from "./WalletPassBuilderPreview"

const PREVIEW_WIDTH = "500px"
const WALLET_PASS_IMAGE_URL_FIELDS = {
  squareLogo: "squareLogoUrl",
  horizontalLogo: "horizontalLogoUrl",
  appleBanner: "appleBannerUrl",
  googleHero: "googleHeroUrl",
} as const

type WalletPassImageField = keyof typeof WALLET_PASS_IMAGE_URL_FIELDS

/** Logos need transparency; the picker filters to PNG and this backs it up. */
const PNG_ONLY_IMAGE_FIELDS: WalletPassImageField[] = ["squareLogo", "horizontalLogo"]

/** Matches the "Up to 2 MB" limit promised in the field descriptions. */
const MAX_IMAGE_BYTES = 2 * 1024 * 1024

const uploadAssetUrl = (url: string) => {
  if (/^https?:\/\//.test(url)) return url

  return new URL(url, import.meta.env.VITE_API_BASE).toString()
}

type Props = {
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
  backPath: string
}

const WalletPassBuilder = ({ event, updateEvent, backPath }: Props) => {
  const history = useHistory()
  const currentAccount = useCurrentAccount()
  const [isSaving, setIsSaving] = useState(false)
  // Shown as placeholders on the optional fields, so an empty input still tells
  // the organizer what the pass will carry.
  const eventName = event.publicEventTitle || event.title
  const eventDateText = formatEventDate(event.startDate, event.timezone)
  const eventTimeText = formatEventTime(event.startDate, event.timezone)
  const eventAddress = formatEventAddress(event)

  const {
    control, handleSubmit, watch, reset, setValue, getValues,
  } = useForm<WalletPassBuilderFormValues>({
    defaultValues: walletPassDataToFormValues(event.walletPassData),
  })

  const uploadedFilesRef = useRef(new WeakSet<File>())
  useEffect(() => {
    reset(walletPassDataToFormValues(event.walletPassData))
  }, [event.walletPassData, reset])

  const headerMode = useWatch({ control, name: "headerMode" })
  const brandNameMode = useWatch({ control, name: "brandNameMode" })
  const eventTitleMode = useWatch({ control, name: "eventTitleMode" })
  const eventLocationMode = useWatch({ control, name: "eventLocationMode" })
  const guestNameEnabled = useWatch({ control, name: "guestNameEnabled" })
  const headerSource = useWatch({ control, name: "headerValueSource" })
  const squareLogo = useWatch({ control, name: "squareLogo" })
  const horizontalLogo = useWatch({ control, name: "horizontalLogo" })
  const appleBanner = useWatch({ control, name: "appleBanner" })
  const googleHero = useWatch({ control, name: "googleHero" })
  const appleBannerUrl = useWatch({ control, name: "appleBannerUrl" })
  const primaryMovedBelowBanner = useWatch({ control, name: "primaryMovedBelowBanner" })
  const [uploadingFields, setUploadingFields] = useState<WalletPassImageField[]>([])

  useEffect(() => {
    const uploads: [WalletPassImageField, File | null][] = [
      ["squareLogo", squareLogo],
      ["horizontalLogo", horizontalLogo],
      ["appleBanner", appleBanner],
      ["googleHero", googleHero],
    ]

    const pendingUpload = uploads.find(([, file]) => (
      file instanceof File && !uploadedFilesRef.current.has(file)
    ))
    if (!pendingUpload) return

    const [field, file] = pendingUpload
    if (!file) return

    // Logos need transparency, which JPEG can't provide — require PNG.
    if (PNG_ONLY_IMAGE_FIELDS.includes(field) && file.type !== "image/png") {
      setValue(field, null)
      errorToast({ title: "Logos must be transparent PNG images" })

      return
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setValue(field, null)
      errorToast({ title: "Images must be 2 MB or smaller" })

      return
    }

    uploadedFilesRef.current.add(file)
    setUploadingFields((fields) => [...fields, field])

    post<{ link: string }>(walletPassUploadsUrl(event.id), { file })
      .then(({ data: { link } }) => {
        setValue(WALLET_PASS_IMAGE_URL_FIELDS[field], uploadAssetUrl(link), {
          shouldDirty: true,
          shouldTouch: true,
        })

        // Apple draws the banner where the Primary field sits, so default to
        // moving Primary below it — into the first secondary slot. Google is
        // unaffected.
        if (field === "appleBanner") {
          setValue("primaryMovedBelowBanner", true, { shouldDirty: true })
        }
      })
      .catch(() => {
        setValue(field, null, { shouldDirty: true })
        errorToast({ title: "Image upload failed" })
      })
      .finally(() => {
        setUploadingFields((fields) => (
          fields.filter((uploadingField) => uploadingField !== field)
        ))
      })
  }, [
    appleBanner,
    event.id,
    getValues,
    googleHero,
    horizontalLogo,
    setValue,
    squareLogo,
  ])

  // The option only applies while a banner is set; removing the banner clears it.
  // Primary then moves into the first secondary slot (see ApplePass), so the
  // auxiliary fields stay entirely the organizer's.
  useEffect(() => {
    if (!appleBannerUrl && primaryMovedBelowBanner) {
      setValue("primaryMovedBelowBanner", false, { shouldDirty: true })
    }
  }, [appleBannerUrl, primaryMovedBelowBanner, setValue])

  const handleCancel = () => {
    history.push(backPath)
  }

  const handleSave = handleSubmit(async (values) => {
    setIsSaving(true)
    try {
      await updateEvent({ walletPassData: formValuesToWalletPassData(values) })
      infoToast({ title: "Wallet pass saved" })
      handleCancel()
    } finally {
      setIsSaving(false)
    }
  })

  return (
    <Modal
      show
      size="full"
      hide={() => {}}
      closeOnEsc={false}
      header={(
        <FullScreenModalNav type="header">
          <Text w={1 / 3}>Pass Builder</Text>
          <HStack spacing={4} w={1 / 3} justify="flex-end">
            <Link onClick={handleCancel} color="gray.500" fontWeight="bold">
              Cancel
            </Link>
            <Link
              onClick={isSaving || uploadingFields.length ? undefined : handleSave}
              color={isSaving || uploadingFields.length ? "gray.400" : "blue.300"}
              fontWeight="bold"
              noOfLines={1}
            >
              {isSaving ? "Saving…" : "Save"}
              {!isSaving && <Icon icon="checkmark" ml={2} />}
            </Link>
          </HStack>
        </FullScreenModalNav>
      )}
      noPadding
    >
      <Flex h="full" overflow="hidden" bg="white">
        {/* Form column */}
        <Box as={SimpleBar as any} flex={1} h="full" minW={0}>
          <Box w="full" px={14} pt={9} pb={10}>

            <PassBuilderSection title="Branding" noDivider primaryTitle>
              <PassBuilderBrandingSection
                control={control}
                watch={watch}
                uploadingFields={uploadingFields}
              />
            </PassBuilderSection>

            <PassBuilderSection title="Colors" titleMb={SECTION_TITLE_MB_COLORS}>
              <SimpleGrid columns={3} gap={10}>
                <PassBuilderColorField
                  name="backgroundColor"
                  label="Background Color"
                  platform="both"
                  control={control}
                />
                <PassBuilderColorField
                  name="textColor"
                  label="Text Color"
                  platform="apple"
                  control={control}
                />
                <PassBuilderColorField
                  name="labelColor"
                  label="Label Color"
                  platform="apple"
                  control={control}
                />
              </SimpleGrid>
            </PassBuilderSection>

            <PassBuilderSection title="Front of the Pass">
              <Stack spacing={SUBSECTION_GAP}>
                <PassBuilderFieldBlock
                  title="Brand Name"
                  platform="both"
                  appleStatus="recommended"
                  googleStatus="required"
                  description={WALLET_PASS_BUILDER_COPY.front.logoText}
                >
                  <PassBuilderFieldMode
                    name="brandNameMode"
                    control={control}
                    options={PASS_BRAND_NAME_MODE_OPTIONS}
                  />
                  <PassBuilderLogoText
                    control={control}
                    accountName={currentAccount.name}
                    disabled={brandNameMode === "default"}
                  />
                </PassBuilderFieldBlock>

                <PassBuilderFieldBlock
                  title="Header Field"
                  platform="apple"
                  appleStatus="recommended"
                  description={WALLET_PASS_BUILDER_COPY.front.headerField}
                >
                  <PassBuilderFieldMode
                    name="headerMode"
                    control={control}
                    options={PASS_HEADER_MODE_OPTIONS}
                  />
                  <PassBuilderLabelRow
                    name="headerLabel"
                    control={control}
                    placeholder={headerMode === "default" ? eventTimeText : undefined}
                    disabled={headerMode === "default"}
                  />
                  <PassBuilderSourceValueRow
                    sourceName="headerValueSource"
                    staticValueName="headerStaticValue"
                    currentSource={headerSource}
                    control={control}
                    staticValuePlaceholder={headerMode === "default" ? eventDateText : undefined}
                    disabled={headerMode === "default"}
                  />
                </PassBuilderFieldBlock>

                <PassBuilderFieldBlock
                  title="Event Title"
                  platform="both"
                  appleStatus="recommended"
                  googleStatus="required"
                  description={WALLET_PASS_BUILDER_COPY.front.eventTitle}
                >
                  <PassBuilderFieldMode
                    name="eventTitleMode"
                    control={control}
                    options={PASS_EVENT_TITLE_MODE_OPTIONS}
                  />
                  <PassBuilderLabelRow
                    name="primaryLabel"
                    control={control}
                    help={WALLET_PASS_BUILDER_COPY.labelHelp.appleOnly}
                  />
                  <PassBuilderTextValueRow
                    name="primaryStaticValue"
                    control={control}
                    placeholder={eventTitleMode === "default" ? eventName : undefined}
                    disabled={eventTitleMode === "default"}
                  />
                  {appleBannerUrl && (
                    <Box mt={3} ml="53px">
                      <Checkbox
                        name="primaryMovedBelowBanner"
                        control={control}
                        mb={0}
                        alignCenter
                        label={(
                          <PassBuilderHelpText>
                            {WALLET_PASS_BUILDER_COPY.eventTitle.banner}
                          </PassBuilderHelpText>
                        )}
                      />
                    </Box>
                  )}
                </PassBuilderFieldBlock>

                <PassBuilderFieldBlock
                  title="Event Location"
                  platform="both"
                  appleStatus="recommended"
                  googleStatus="recommended"
                  description={WALLET_PASS_BUILDER_COPY.front.eventLocation}
                >
                  <PassBuilderFieldMode
                    name="eventLocationMode"
                    control={control}
                    options={PASS_EVENT_LOCATION_MODE_OPTIONS}
                  />
                  <PassBuilderLabelRow
                    name="secondaryLabel"
                    control={control}
                    help={WALLET_PASS_BUILDER_COPY.labelHelp.appleOnly}
                  />
                  <PassBuilderTextValueRow
                    name="secondaryStaticValue"
                    control={control}
                    placeholder={
                      eventLocationMode === "default" ? event.venue || eventAddress : undefined
                    }
                    disabled={eventLocationMode === "default"}
                  />
                </PassBuilderFieldBlock>

                <PassBuilderFieldBlock
                  title="Guest Name"
                  platform="both"
                  appleStatus="recommended"
                  googleStatus="recommended"
                  description={WALLET_PASS_BUILDER_COPY.front.guestName}
                  rightElement={(
                    <Switch
                      size="lg"
                      isChecked={guestNameEnabled}
                      onChange={(e) => setValue(
                        "guestNameEnabled",
                        e.target.checked,
                        { shouldDirty: true },
                      )}
                      colorScheme="blue"
                      flexShrink={0}
                    />
                  )}
                >
                  <PassBuilderLabelRow
                    name="guestNameLabel"
                    control={control}
                    help={(
                      <>
                        {`• ${WALLET_PASS_BUILDER_COPY.labelHelp.appleOnly}`}
                        <br />
                        {`• ${WALLET_PASS_BUILDER_COPY.labelHelp.googleTicketHolder}`}
                      </>
                    )}
                    placeholder={DEFAULT_GUEST_NAME_LABEL}
                    disabled={!guestNameEnabled}
                  />
                </PassBuilderFieldBlock>

                <PassBuilderFieldBlock
                  title="Auxiliary Fields"
                  platform="both"
                  appleStatus="optional"
                  googleStatus="optional"
                  description={WALLET_PASS_BUILDER_COPY.front.auxiliaryFields}
                >
                  <PassBuilderAuxiliaryField control={control} />
                </PassBuilderFieldBlock>

                <PassBuilderQrToggle control={control} />
              </Stack>
            </PassBuilderSection>

            <PassBuilderSection title="Back of the Pass">
              <PassBuilderFieldBlock
                title="Additional Information"
                platform="both"
                appleStatus="optional"
                googleStatus="optional"
                description={WALLET_PASS_BUILDER_COPY.back.additionalInformation}
              >
                <PassBuilderBackSections control={control} />
              </PassBuilderFieldBlock>
            </PassBuilderSection>

          </Box>
        </Box>

        <Box
          as={SimpleBar as any}
          flexShrink={0}
          w={PREVIEW_WIDTH}
          h="full"
          bg="gray.50"
          borderLeftWidth={1}
          borderColor="gray.300"
        >
          <WalletPassBuilderPreview
            watch={watch}
            eventName={eventName}
            eventAccountName={currentAccount.name}
            eventDateText={eventDateText}
            eventTimeText={eventTimeText}
            eventVenue={event.venue}
            eventAddress={eventAddress}
          />
        </Box>
      </Flex>
    </Modal>
  )
}

export default WalletPassBuilder
